import { DesignerVerificationStatus } from '@repo/graphql-types'

export const COOLDOWN_DAYS = 45

export interface DesignerVerificationData {
  status?: DesignerVerificationStatus | null
  requestDate?: string | null
}

export interface CooldownCalculation {
  daysRemaining: number
  cooldownPassed: boolean
  isRejected: boolean
  isPending: boolean
}

/**
 * Calculates the cooldown period for designer verification
 * @param verificationData - The designer verification status and request date
 * @returns Object with cooldown information
 */
export const calculateDesignerVerificationCooldown = (
  verificationData?: DesignerVerificationData | null,
): CooldownCalculation => {
  let daysRemaining = 0

  if (verificationData?.requestDate) {
    const requestDate = new Date(verificationData.requestDate)
    const today = new Date()
    const timeDiff = today.getTime() - requestDate.getTime()
    const daysPassed = Math.floor(timeDiff / (1000 * 3600 * 24))
    daysRemaining = COOLDOWN_DAYS - daysPassed

    if (daysRemaining < 0) {
      daysRemaining = 0
    }
  }

  const cooldownPassed = daysRemaining === 0
  const isRejected = verificationData?.status === DesignerVerificationStatus.Rejected
  const isPending = verificationData?.status === DesignerVerificationStatus.Pending

  return {
    daysRemaining,
    cooldownPassed,
    isRejected,
    isPending,
  }
}

/**
 * Determines if a user can apply to become a designer based on their verification status
 * @param verificationData - The designer verification status and request date
 * @returns true if the user can apply
 */
export const canUserApplyToBeDesigner = (
  verificationData?: DesignerVerificationData | null,
): boolean => {
  const { cooldownPassed, isRejected, isPending } =
    calculateDesignerVerificationCooldown(verificationData)

  // If pending, user cannot apply
  if (isPending) {
    return false
  }

  // If rejected but cooldown has passed, user can apply again
  if (isRejected && cooldownPassed) {
    return true
  }

  // If rejected but cooldown hasn't passed, user cannot apply
  if (isRejected && !cooldownPassed) {
    return false
  }

  // Default case: user can apply
  return true
}
